import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import { ReactNode, createContext, useEffect, useMemo, useState } from 'react';
import { getZoneGuid, getZoneUrlSplit } from '../shared/zoneHelpers';

import Auth from '../stores/authentication';
import CheckmateZoneApiClient from '../services/zoneApiClient';
import { IOrganizationRoleModel } from '../interfaces/IUser';
import { LocalRoutes } from '../utilities/LocalRoutes';
import { SameCaseModal } from '../components/shared/modals/SameCaseModal';
import { ZoneContextType } from '../utilities/Constants';
import { useAuthContext } from './AuthContext';

const ZoneContext = createContext<ZoneContextType | undefined>(undefined);

const ZoneProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [zoneGuid, setZoneGuid] = useState<string>('');
    const [zoneName, setZoneName] = useState<string>('');
    const [zoneApiClient, setZoneApiClient] = useState<CheckmateZoneApiClient>(
        new CheckmateZoneApiClient('')
    );
    const [sameCaseGuid, setSameCaseGuid] = useState<string | undefined>(undefined);

    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthContext();

    const defaultOrganization = useMemo(() => {
        const t = user.organizationRoles?.filter(
            (organizationRole: IOrganizationRoleModel) => organizationRole.default === true
        )[0];
        return t;
    }, [user]);

    const zoneNavigate = (path: string, options?: NavigateOptions) => {
        navigate(buildZoneUrl(path), options);
    };

    const buildZoneUrl = (path: string) => {
        return `/zone/${zoneGuid}${path}`;
    };

    const buildZoneUrlByOrgGuid = (orgGuid:string, path: string) => {
        return `/zone/${orgGuid}${path}`;
    };

    const zoneChange = async (
        organizationGuid: string,
        organizationName: string,
        redirectPath?: string
    ) => {
        let newUrl = '';

        if (window.location.pathname.includes('/zone/')) {
            const zoneSplit = getZoneUrlSplit(window.location.pathname);
            newUrl = `${zoneSplit[0]}zone/${organizationGuid}`;
        } else {
            newUrl = `${window.location.origin}/zone/${organizationGuid}`;
        }

        if (redirectPath) {
            newUrl += redirectPath;
        }

        if (zoneGuid === '') {
            setupZone(organizationGuid, organizationName);
            const newRoute = `zone/${organizationGuid}`;
            navigate(newRoute);
        } else {
            window.open(newUrl, '_blank')?.focus();
        }
    };

    const setupZone = (organizationGuid: string, organizationName: string) => {
        setZoneGuid(organizationGuid);
        setZoneName(organizationName);
        setZoneApiClient(new CheckmateZoneApiClient(organizationGuid));
    };

    const handleSameCaseModalClose = () => {
        setSameCaseGuid(undefined);
    };

    const showSameCaseModal = (sameCaseGuid: string) => {
        setSameCaseGuid(sameCaseGuid);
    };

    const caseFileModelId = user?.organizationRoles?.filter(
        (organizationRole: IOrganizationRoleModel) => organizationRole.guid === zoneGuid
    )[0]?.caseFileModelId;

    useEffect(() => {
        const zoneUrlGuid = getZoneGuid(window.location.pathname);
        setZoneGuid(zoneUrlGuid);
        setZoneApiClient(new CheckmateZoneApiClient(zoneUrlGuid));
    }, [window.location]);

    useEffect(() => {
        if (user.active && zoneGuid) {
            const zoneOrganization = user?.organizationRoles?.filter(
                (organizationRole: IOrganizationRoleModel) => organizationRole.guid === zoneGuid
            )[0];
            if (!zoneOrganization) {
                navigate(LocalRoutes.NotFound);
            }
        }
    }, [user, zoneGuid]);

    useEffect(() => {
        if (user.active) {
            const zoneOrganization = user?.organizationRoles?.filter(
                (organizationRole: IOrganizationRoleModel) => organizationRole.guid === zoneGuid
            )[0];
            const zoneOrganizationName =
                zoneOrganization?.shortName ??
                zoneOrganization?.displayName ??
                zoneOrganization?.name;
                
            const defaultOrganizationGuid = defaultOrganization ? defaultOrganization.guid! : '';
            const defaultOrganizationName = defaultOrganization
                ? (defaultOrganization.shortName! ??
                  defaultOrganization.displayName! ??
                  defaultOrganization.name!)
                : '';
            const currentZoneGuid = zoneOrganization?.guid ?? defaultOrganizationGuid;
            const zoneName = zoneOrganizationName ?? defaultOrganizationName;
            const isRoot = location.pathname === LocalRoutes.Root;

            if (!Auth.isSystemAdmin(user)) {
                if (isRoot) {
                    navigate(LocalRoutes.Zone.replace(':zoneGuid', currentZoneGuid));
                }
                setupZone(currentZoneGuid, zoneName);
            }
        }
    }, [user, zoneGuid, location, defaultOrganization]);

    return (
        <ZoneContext.Provider
            value={{
                buildZoneUrl,
                setupZone,
                showSameCaseModal,
                zoneGuid,
                zoneName,
                zoneChange,
                zoneApiClient,
                zoneNavigate,
                buildZoneUrlByOrgGuid
            }}
        >
            {children}
            {sameCaseGuid && (
                <SameCaseModal
                    cancelText={'OK'}
                    onCancel={handleSameCaseModalClose}
                    onClose={handleSameCaseModalClose}
                    open={!!sameCaseGuid}
                    params={{
                        sameCaseGuid: sameCaseGuid || '',
                        caseFileModelId: caseFileModelId ?? 0,
                    }}
                    title={'Same Case Group'}
                />
            )}
        </ZoneContext.Provider>
    );
};

export { ZoneProvider, ZoneContext };
